/**
 * @fileoverview Wrapper component for drawer content with consistent
 * loading, error, and empty state handling. Reduces duplication across
 * BookmarksDrawer and TagsDrawer.
 */

import type { ReactElement, ReactNode } from 'react';
import { Alert } from '@mui/material';
import { EmptyState, LoadingSpinner } from '@/shared/components';

export interface DrawerContentWrapperProps<T> {
  /** Whether data is currently loading */
  loading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** The data to display */
  data: readonly T[] | T[] | undefined;
  /** Render function for when data is available */
  renderContent: (data: readonly T[]) => ReactNode;
  /** Props for the empty state */
  emptyState: {
    icon: ReactElement;
    title: string;
    subtitle: string;
  };
  /** Optional custom error message */
  errorMessage?: string;
}

/**
 * Wrapper component that handles loading, error, and empty states for drawer content.
 * Provides consistent UI patterns across different drawer components.
 */
export function DrawerContentWrapper<T>({
  loading,
  error,
  data,
  renderContent,
  emptyState,
  errorMessage,
}: DrawerContentWrapperProps<T>): ReactElement {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {errorMessage ?? error.message}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={emptyState.icon}
        title={emptyState.title}
        subtitle={emptyState.subtitle}
      />
    );
  }

  return <>{renderContent(data)}</>;
}
